import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { dashboardFor } from "@/components/ProtectedRoute";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "For Members", path: "/for-members" },
  { label: "For Partners", path: "/for-partners" },
  { label: "Sports", path: "/sports" },
  { label: "Find Facilities", path: "/facilities" },
  { label: "About", path: "/about" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const authed = user && user !== false;

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-300 border-b ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-zinc-800/80" : "bg-transparent border-transparent"
      }`}
    >
      <nav className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="flex items-center group" data-testid="navbar-brand-logo">
          <Logo size="h-7 lg:h-9" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={`text-sm font-medium transition-colors hover:text-white ${
                location.pathname === l.path ? "text-[#E53E3E]" : "text-zinc-400"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {authed ? (
            <>
              <Button
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 hover:text-white rounded-full"
                onClick={() => navigate(dashboardFor(user.role))}
                data-testid="navbar-dashboard-button"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" onClick={handleLogout} data-testid="navbar-logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 hover:text-white rounded-full"
                onClick={() => navigate("/login")}
                data-testid="navbar-login-button"
              >
                Login
              </Button>
              <Button className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" onClick={() => navigate("/get-started")} data-testid="navbar-get-started-button">
                Get Started
              </Button>
            </>
          )}
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="p-2 text-white" data-testid="navbar-mobile-toggle" aria-label="Open menu">
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0A0A0B] border-zinc-800 w-[82%] max-w-sm p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-zinc-800">
                  <Logo size="h-8" />
                </div>
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
                  {NAV_LINKS.map((l) => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={() => setOpen(false)}
                      className="px-4 py-3 rounded-lg text-base font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
                <div className="p-4 border-t border-zinc-800 flex flex-col gap-3">
                  {authed ? (
                    <>
                      <Button variant="outline" className="border-zinc-700 bg-transparent rounded-full" onClick={() => { setOpen(false); navigate(dashboardFor(user.role)); }}>
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                      </Button>
                      <Button className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" onClick={handleLogout}>
                        <LogOut className="w-4 h-4 mr-2" /> Logout
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="border-zinc-700 bg-transparent rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>
                        Login
                      </Button>
                      <Button className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" onClick={() => { setOpen(false); navigate("/get-started"); }}>
                        Get Started
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
